import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useCreateCountry, useUpdateCountry, useDeleteCountry, useCountries } from "@/hooks/useCountries";
import { Country } from "@/types/country";
import { toast } from "sonner";
interface CountryDialogProps {
  country?: Country;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}
const weekDays = [{
  value: "monday",
  label: "الإثنين"
}, {
  value: "tuesday",
  label: "الثلاثاء"
}, {
  value: "wednesday",
  label: "الأربعاء"
}, {
  value: "thursday",
  label: "الخميس"
}, {
  value: "friday",
  label: "الجمعة"
}, {
  value: "saturday",
  label: "السبت"
}, {
  value: "sunday",
  label: "الأحد"
}];
export const CountryDialog = ({
  country,
  trigger,
  onSuccess,
  open: controlledOpen,
  onOpenChange
}: CountryDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [formData, setFormData] = useState({
    name_ar: "",
    name_en: "",
    default_currency: "",
    weekend_days: []
  });
  
  const { data: countries = [] } = useCountries();
  const createCountry = useCreateCountry();
  const updateCountry = useUpdateCountry();
  const deleteCountry = useDeleteCountry();

  // تحديث البيانات عند تغيير country prop
  useEffect(() => {
    if (country) {
      setFormData({
        name_ar: country.name_ar || "",
        name_en: country.name_en || "",
        default_currency: country.default_currency || "",
        weekend_days: country.weekend_days || []
      });
    }
  }, [country]);

  // تفريغ البيانات عند فتح الحوار للإضافة
  useEffect(() => {
    if (open && !country) {
      setFormData({
        name_ar: "",
        name_en: "",
        default_currency: "",
        weekend_days: []
      });
    }
  }, [open, country]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // التحقق من عدم التكرار
    const existingCountry = countries.find(c => 
      c.name_ar.toLowerCase() === formData.name_ar.toLowerCase() && 
      (!country || c.id !== country.id)
    );
    
    if (existingCountry) {
      toast.error("هذه الدولة موجودة بالفعل");
      return;
    }

    try {
      if (country) {
        await updateCountry.mutateAsync({
          id: country.id,
          ...formData
        });
        toast.success("تم تحديث الدولة بنجاح");
      } else {
        await createCountry.mutateAsync(formData);
        toast.success("تم إضافة الدولة بنجاح");
      }
      setOpen(false);
      onSuccess?.();
      if (!country) {
        setFormData({
          name_ar: "",
          name_en: "",
          default_currency: "",
          weekend_days: []
        });
      }
    } catch (error) {
      toast.error("حدث خطأ أثناء حفظ البيانات");
    }
  };
  const handleDelete = async () => {
    if (!country) return;
    try {
      await deleteCountry.mutateAsync(country.id);
      toast.success("تم حذف الدولة بنجاح");
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      toast.error("حدث خطأ أثناء حذف الدولة");
    }
  };
  const handleWeekendDayChange = (day: string) => {
    const currentDays = formData.weekend_days || [];
    if (currentDays.includes(day)) {
      setFormData({
        ...formData,
        weekend_days: currentDays.filter(d => d !== day)
      });
    } else {
      setFormData({
        ...formData,
        weekend_days: [...currentDays, day]
      });
    }
  };
  return <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{country ? "تعديل الدولة" : "إضافة دولة جديدة"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name_ar">الاسم بالعربية *</Label>
            <Input id="name_ar" value={formData.name_ar} onChange={e => setFormData({
            ...formData,
            name_ar: e.target.value
          })} required />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
            <Input id="name_en" value={formData.name_en} onChange={e => setFormData({
            ...formData,
            name_en: e.target.value
          })} />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="default_currency">العملة الافتراضية</Label>
            <Input id="default_currency" value={formData.default_currency} onChange={e => setFormData({
            ...formData,
            default_currency: e.target.value
          })} placeholder="مثال: USD, EUR, SAR" />
          </div>
          
          <div className="space-y-2">
            <Label>عطلة نهاية الأسبوع</Label>
            <div className="space-y-2">
              {weekDays.map(day => <label key={day.value} className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.weekend_days?.includes(day.value) || false} onChange={() => handleWeekendDayChange(day.value)} className="rounded" />
                  <span className="text-sm">{day.label}</span>
                </label>)}
            </div>
          </div>
          
          <div className="flex justify-between gap-2">
            {country && <Button type="button" variant="destructive" onClick={handleDelete} className="gap-2">
                <Trash2 className="w-4 h-4" />
                حذف
              </Button>}
            <div className="flex gap-2 mr-auto">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                إلغاء
              </Button>
              <Button type="submit">
                {country ? "تحديث" : "إضافة"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>;
};